"use client";

import { backOfficeActions } from "@/backoffice/actions";
import { Nav } from "@/components";
import { useI18N } from "@/providers";
import { detectTypeOfString } from "@/utils/typeof";
import {
  Suspense,
  startTransition,
  useActionState,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { LayoutContext, layoutList } from "./LayoutContext";

import type { FetchParams, RequestResponse, User } from "@/types";
import type { JSX, ReactNode } from "react";
import type { LayoutType } from "./LayoutContext";

/** Keys used to persist layout preferences in localStorage. */
const STORAGE_KEY_DIR = "dir";
const STORAGE_KEY_LAYOUT = "layout";
/** Backend endpoint (allow-listed in `backOfficeActions`) for the current user. */
const AUTH_ME_ENDPOINT = "auth/me";

const DEFAULT_DIR = "ltr";
const DEFAULT_LAYOUT: LayoutType = "Vertical";

/** Narrows an arbitrary stored string to a valid `LayoutType`. */
const toLayout = (value: string | null): LayoutType =>
  layoutList.includes(value as LayoutType)
    ? (value as LayoutType)
    : DEFAULT_LAYOUT;

/**
 * Props for the LayoutProvider component.
 */
interface LayoutProviderProps {
  children: ReactNode;
}

/**
 * LayoutProvider component that manages the global layout state, user authentication, and translations.
 *
 * @param {LayoutProviderProps} props - The component props.
 * @returns {JSX.Element | null} The accessible context provider or null if not mounted.
 */
const LayoutProvider = ({ children }: LayoutProviderProps): JSX.Element => {
  const { t: translate } = useI18N();
  const [dir, setDir] = useState<string>(DEFAULT_DIR);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [layout, setLayout] = useState<LayoutType>(DEFAULT_LAYOUT);
  const [sidebarIsOpen, setSidebarIsOpen] = useState<boolean>(false);
  const [me, getMe] = useActionState<
    RequestResponse<Partial<User> | null> | null,
    FetchParams
  >(backOfficeActions, null);

  // Runs client-side only (effects never run during SSR), so localStorage is
  // safe to read directly — no `typeof window` guard needed.
  useEffect(() => {
    setDir(localStorage.getItem(STORAGE_KEY_DIR) || DEFAULT_DIR);
    setLayout(toLayout(localStorage.getItem(STORAGE_KEY_LAYOUT)));
    startTransition(() => {
      getMe({ url: AUTH_ME_ENDPOINT });
    });
  }, [getMe]);

  /**
   * Helper function to translate keys, capitalizing the first letter and replacing underscores with spaces if no translation is found.
   *
   * @param {string} key - The translation key.
   * @returns {string} The translated string or formatted key.
   */
  const t = useCallback(
    (key: string): string => {
      const translated = translate(detectTypeOfString(key));
      // `translate` echoes the raw key back when there's no matching resource;
      // in that case humanize it (capitalize + underscores → spaces).
      if (translated === key) {
        return (key.charAt(0).toUpperCase() + key.slice(1)).replaceAll(
          "_",
          " "
        );
      }
      return translated;
    },
    [translate]
  );

  const changeDir = useCallback((newDir: string) => {
    setDir(newDir);
    localStorage.setItem(STORAGE_KEY_DIR, newDir);
  }, []);

  const changeLayout = useCallback((newLayout: LayoutType) => {
    setLayout(newLayout);
    localStorage.setItem(STORAGE_KEY_LAYOUT, newLayout);
  }, []);

  const values = useMemo(
    () => ({
      t,
      // `me` carries the response under its endpoint key (see `proxyToBackend`);
      // that slot is the user payload, hence the narrowing cast.
      userAuth: (me?.[AUTH_ME_ENDPOINT] as Partial<User> | null) ?? null,
      layout,
      changeLayout,
      dir,
      changeDir,
      showModal,
      setShowModal,
      sidebarIsOpen,
      setSidebarIsOpen
    }),
    [t, me, layout, changeLayout, dir, changeDir, showModal, sidebarIsOpen]
  );

  return (
    <LayoutContext.Provider value={values}>
      <Suspense fallback={null}>
        <Nav />
        {children}
      </Suspense>
    </LayoutContext.Provider>
  );
};

export default LayoutProvider;
